import { ApplicationSubPageContentContainer } from "../ApplicationSubPageContentContainer";
import { Grid } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAxios } from "../../../shared/technical/rest/AxiosProvider";
import { useAccount } from "../../users/AccountProvider";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Application } from "../Application";
import { getTermsAndConditions, updateTermsAndConditions } from "./TermsAndConditionsApi";
import { TermsAndConditions, termsAndConditionsSchema } from "./TermsAndConditions";
import { FormProvider, useForm } from "react-hook-form";
import { RhfForm } from "../../../shared/components/forms/RhfForm";
import { FormHeader } from "../../../shared/components/forms/FormHeader";
import { mayEditApplication } from "../permissions";
import { yupResolver } from "@hookform/resolvers/yup";
import { MuiRhfTextField } from "../../../shared/components/forms/MuiRhfTextField";

export function TermsAndConditionsSubPage(props: {
  application: Application,
}) {
  const { application } = props;
  const { t } = useTranslation();
  const axios = useAxios();
  const account = useAccount();
  const queryClient = useQueryClient();
  const [formReadonly, setFormReadonly] = useState<boolean>(true);

  const { data } = useQuery('termsAndConditions', () => getTermsAndConditions(axios, application.id!!), {
    keepPreviousData: true,
    refetchOnWindowFocus: false
  });
  const updateTermsAndConditionsMutation = useMutation(
      (termsAndConditions: TermsAndConditions) => updateTermsAndConditions(axios, application.id!!, termsAndConditions),
      {
        onSuccess: () => queryClient.invalidateQueries('termsAndConditions').then()
      });
  const form = useForm<TermsAndConditions>({
    mode: 'onBlur',
    defaultValues: termsAndConditionsSchema(t).getDefault(),
    resolver: yupResolver(termsAndConditionsSchema(t))
  });

  const saveTermsAndConditions = useCallback((termsAndConditions: TermsAndConditions) => {
    updateTermsAndConditionsMutation.mutate(termsAndConditions);
    setFormReadonly(true);
  }, [updateTermsAndConditionsMutation]);

  useEffect(() => {
    if (data) {
      form.reset(data);
    }
  }, [data, form]);

  return (
      <ApplicationSubPageContentContainer>
        <FormProvider {...form} >
          <RhfForm id={'terms-and-conditions-form'} onSubmit={saveTermsAndConditions}>
            <FormHeader title={t('ApplicationPage.subPageTermsAndConditions')}
                        onCancel={() => {
                          form.reset();
                          setFormReadonly(true);
                        }}
                        onDelete={() => {
                        }}
                        canBeEdited={mayEditApplication(account, props.application)}
                        readonly={formReadonly}
                        onStartEditing={() => setFormReadonly(false)}
                        hideDialogControls={true}
            />
            <Grid container={true} spacing={3} sx={{ padding: 1, width: '100%' }}>
              <Grid item={true} xs={12} lg={6}>
                <MuiRhfTextField name={'version'}
                                 label={t('TermsAndConditions.version')}
                                 fullWidth={true}
                                 disabled={formReadonly}
                />
              </Grid>
              <Grid item={true} xs={12} lg={12}>
                <MuiRhfTextField name={'htmlText'}
                                 label={t('TermsAndConditions.htmlText')}
                                 placeholder={t("TermsAndConditions.htmlTextHint")}
                                 multiline={true}
                                 rows={10}
                                 InputLabelProps={{ shrink: true }}
                                 fullWidth={true}
                                 disabled={formReadonly}
                />
              </Grid>
            </Grid>
          </RhfForm>
        </FormProvider>
      </ApplicationSubPageContentContainer>
  )
}
